import type { ChangeEvent, FormEvent } from 'react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { useApiClient } from '../hooks/useApiClient';
import { useAuth } from '../hooks/useAuth';
import useBreakpoint from '../hooks/useBreakpoint';

interface ConfigPanelProps {
  open: boolean;
  onClose: () => void;
}

type FormState = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

const INITIAL_FORM: FormState = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

const MIN_PASSWORD_LENGTH = 12;

export default function ConfigPanel({ open, onClose }: ConfigPanelProps) {
  const apiClient = useApiClient();
  const { session } = useAuth();
  const { isMobile, isTablet } = useBreakpoint();
  const [formState, setFormState] = useState<FormState>(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setFormState(INITIAL_FORM);
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const handleChange = (field: keyof FormState) => (event: ChangeEvent<HTMLInputElement>) => {
    setFormState((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (formState.newPassword.length < MIN_PASSWORD_LENGTH) {
      toast.error(`New password must be at least ${MIN_PASSWORD_LENGTH} characters long`);
      return;
    }
    if (formState.newPassword !== formState.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await apiClient('/api/admin/account/change-password', {
        method: 'POST',
        body: JSON.stringify({
          currentPassword: formState.currentPassword,
          newPassword: formState.newPassword,
        }),
      });
      if (!response.ok) {
        let message = 'Unable to update password';
        try {
          const data = (await response.json()) as { detail?: string };
          if (typeof data.detail === 'string') {
            message = data.detail;
          }
        } catch {
          // ignore JSON parse errors
        }
        throw new Error(message);
      }
      toast.success('Password updated');
      setFormState(INITIAL_FORM);
      onClose();
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Unable to update password');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.75)',
        backdropFilter: 'blur(12px)',
        zIndex: 30,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: isMobile ? '1.25rem' : isTablet ? '1.75rem' : '2rem',
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: isMobile ? '100%' : '520px',
          borderRadius: isMobile ? '1.25rem' : '1.5rem',
          border: '1px solid rgba(148, 163, 184, 0.25)',
          background: 'rgba(15, 23, 42, 0.92)',
          boxShadow: '0 35px 80px -35px rgba(30, 64, 175, 0.8)',
          padding: isMobile ? '1.5rem' : '2rem',
          color: '#e2e8f0',
        }}
      >
        <header style={{ marginBottom: isMobile ? '1.25rem' : '1.5rem' }}>
          <h2 style={{ margin: 0, fontSize: isMobile ? '1.35rem' : '1.5rem', fontWeight: 600 }}>Account Security</h2>
          <p style={{ margin: '0.5rem 0 0', color: '#94a3b8', lineHeight: 1.5 }}>
            Update your operator password. Other active sessions will be signed out after the change.
          </p>
          <p style={{ margin: '0.35rem 0 0', color: '#64748b', fontSize: '0.85rem' }}>
            Signed in as{' '}
            <span style={{ color: '#e2e8f0', fontWeight: 600 }}>
              {session?.user.displayName ?? session?.user.username ?? 'operator'}
            </span>
            .
          </p>
        </header>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <span style={{ fontWeight: 600, letterSpacing: '0.03em', fontSize: '0.85rem', textTransform: 'uppercase', color: '#94a3b8' }}>
              Current Password
            </span>
            <input
              type="password"
              value={formState.currentPassword}
              onChange={handleChange('currentPassword')}
              placeholder="Current password"
              style={{
                borderRadius: '0.9rem',
                border: '1px solid rgba(148, 163, 184, 0.35)',
                padding: '0.85rem 1rem',
                background: 'rgba(15, 23, 42, 0.6)',
                color: '#e2e8f0',
                fontSize: '1rem',
              }}
              required
              disabled={isSubmitting}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <span style={{ fontWeight: 600, letterSpacing: '0.03em', fontSize: '0.85rem', textTransform: 'uppercase', color: '#94a3b8' }}>
              New Password
            </span>
            <input
              type="password"
              value={formState.newPassword}
              onChange={handleChange('newPassword')}
              placeholder="New password"
              style={{
                borderRadius: '0.9rem',
                border: '1px solid rgba(148, 163, 184, 0.35)',
                padding: '0.85rem 1rem',
                background: 'rgba(15, 23, 42, 0.6)',
                color: '#e2e8f0',
                fontSize: '1rem',
              }}
              minLength={MIN_PASSWORD_LENGTH}
              required
              disabled={isSubmitting}
            />
            <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
              Minimum {MIN_PASSWORD_LENGTH} characters. Include numbers and symbols for additional security.
            </span>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <span style={{ fontWeight: 600, letterSpacing: '0.03em', fontSize: '0.85rem', textTransform: 'uppercase', color: '#94a3b8' }}>
              Confirm New Password
            </span>
            <input
              type="password"
              value={formState.confirmPassword}
              onChange={handleChange('confirmPassword')}
              placeholder="Repeat new password"
              style={{
                borderRadius: '0.9rem',
                border: '1px solid rgba(148, 163, 184, 0.35)',
                padding: '0.85rem 1rem',
                background: 'rgba(15, 23, 42, 0.6)',
                color: '#e2e8f0',
                fontSize: '1rem',
              }}
              minLength={MIN_PASSWORD_LENGTH}
              required
              disabled={isSubmitting}
            />
          </label>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexDirection: isMobile ? 'column-reverse' : 'row',
              gap: isMobile ? '0.75rem' : '1rem',
              marginTop: '0.5rem',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              style={{
                borderRadius: '9999px',
                border: '1px solid rgba(148, 163, 184, 0.35)',
                background: 'transparent',
                color: '#cbd5f5',
                padding: '0.75rem 1.25rem',
                fontWeight: 600,
                letterSpacing: '0.05em',
                cursor: 'pointer',
                width: isMobile ? '100%' : 'auto',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                borderRadius: '9999px',
                border: 'none',
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.85), rgba(37, 99, 235, 0.95))',
                color: '#f8fafc',
                padding: '0.75rem 1.75rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                width: isMobile ? '100%' : 'auto',
                boxShadow: '0 25px 40px -25px rgba(59, 130, 246, 0.55)',
                opacity: isSubmitting ? 0.7 : 1,
              }}
            >
              {isSubmitting ? 'Updating…' : 'Update Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
